import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    Loader2,
    FileText,
    Download,
    Eye,
    Calendar,
    DollarSign,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { adminService } from "../../services/adminService";
import { documentService } from "../../services/documentService";
import { formatDateFromDB } from "../../utils/dateUtils";
import { formatInvoiceNumber } from "../../utils/formatters";

export function FaturaDetalhes() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [fatura, setFatura] = useState<any>(null);
    const [documentos, setDocumentos] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!isAuthenticated) navigate("/area-cliente");
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (id) loadFatura();
    }, [id]);

    const loadFatura = async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            const [faturaData, docs] = await Promise.all([
                adminService.getFatura(id),
                adminService.getFaturaDocumentos(id),
            ]);
            setFatura(faturaData);
            setDocumentos(docs);
        } catch (error) {
            console.error("Erro ao carregar fatura:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatCurrency = (value: number) =>
        new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

    const formatDate = (date: string) => formatDateFromDB(date);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-brand animate-spin" />
            </div>
        );
    }

    if (!fatura) {
        return (
            <div className="min-h-screen">
                <div className="py-8 text-center">
                    <p className="text-muted-foreground">Fatura não encontrada</p>
                    <Link to="/portal/faturas" className="text-brand hover:text-brand/80 mt-4 inline-block">
                        Voltar para Faturas
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{`Fatura ${formatInvoiceNumber(fatura.numero)} | Portal do Cliente`}</title>
            </Helmet>

            <div className="min-h-screen">
                <div className="py-8">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                            <span className="bg-gradient-to-r from-green-500 to-lime-400 bg-clip-text text-transparent">
                                Fatura {formatInvoiceNumber(fatura.numero)}
                            </span>
                        </h1>
                    </motion.div>

                    {/* Informações da Fatura */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="rounded-xl bg-card border border-border p-6 mb-6"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Data de Emissão</p>
                                <p className="text-foreground font-medium flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-brand" />
                                    {formatDate(fatura.dataEmissao)}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Data de Vencimento</p>
                                <p className="text-foreground font-medium flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-orange-500" />
                                    {formatDate(fatura.dataVencimento)}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Valor Total</p>
                                <p className="text-2xl font-bold text-green-500 flex items-center gap-2">
                                    <DollarSign className="w-5 h-5" />
                                    {formatCurrency(Number(fatura.valorTotal))}
                                </p>
                            </div>
                        </div>

                        {fatura.observacoes && (
                            <div className="pt-4 border-t border-border">
                                <p className="text-sm text-muted-foreground mb-2">Observações</p>
                                <p className="text-foreground">{fatura.observacoes}</p>
                            </div>
                        )}
                    </motion.div>

                    {/* Documentos Vinculados */}
                    {documentos.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="rounded-xl bg-card border border-border p-6"
                        >
                            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                                <FileText className="w-5 h-5 text-purple-500" />
                                Documentos Vinculados
                            </h2>
                            <div className="space-y-3">
                                {documentos.map((doc) => (
                                    <div
                                        key={doc.id}
                                        className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border hover:bg-muted/80 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <FileText className="w-5 h-5 text-brand" />
                                            <div>
                                                <p className="text-foreground font-medium">{doc.nome}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {doc.tipoDocumento?.nome} • {formatDate(doc.dataReferencia)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => documentService.viewFile(doc.id)}
                                                className="p-2 rounded-lg bg-brand/10 text-brand hover:bg-brand/20 transition-colors"
                                                title="Visualizar"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => documentService.downloadFile(doc.id, doc.nome)}
                                                className="p-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
                                                title="Download"
                                            >
                                                <Download className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>
        </>
    );
}
